import { useSearch } from '@tanstack/react-router'
import {
  useOpenNewProcessDataView,
  useOpenSavedChartResource,
  useOpenTrendResource,
} from 'features/facilityModel/useFacilityModelNavigation'
import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import {
  DocumentTab,
  type FragmentHighlight,
  SearchHistoryItem,
  type SearchStateUpdate,
} from 'features/knowledgeDocumentViewer/types'
import {
  ChartSearchItem,
  documentTableRowToSearchItem,
  RecentItem,
  SearchableItem,
  SensorSearchItem,
} from 'features/knowledgeDocumentViewer/types/searchableItems'
import React, { createContext, useCallback, useContext, useMemo } from 'react'

const PROCESS_DATA_LABEL = 'Process Data'

export type SelectDocumentOptions = {
  documentId: string
  query?: string
  fragmentHighlight?: FragmentHighlight
  searchOriginQuery?: string
  pageNumber?: number
}

export type OpenNewTabOptions = SelectDocumentOptions

export interface SearchActionsContextType {
  onSelectDocument: (options: SelectDocumentOptions) => void
  onOpenNewTab: (options: OpenNewTabOptions) => void
  onOpenChatbot: () => void
  onSearchStateChange: (
    updates: SearchStateUpdate,
    options?: { tabId?: string }
  ) => void
  onAddRecentDocument: (document: DocumentTableRow) => void
  onAddRecentAsset: (asset: SearchableItem) => void
  onOpenSensorDashboard: (sensor: SensorSearchItem) => void
  onOpenChartDashboard: (chart: ChartSearchItem) => void
  onOpenAssetDashboard: (datapointIds: string[], label: string) => void
  onOpenLinkedDocuments: (
    documentIds: string[],
    referenceText: string,
    unitId?: string
  ) => void
  onOpenTagSearchDashboard: () => void
  openTabs: DocumentTab[]
  onSwitchToTab: (tabId: string) => void
  recentItems: RecentItem[]
  onSnapshotSearchResults: (tabId: string, results: DocumentTableRow[]) => void
  favoritesDisabled?: boolean
  favoritesSaving?: boolean
  isFavorited: (id: string) => boolean
  toggleFavorite: (doc: DocumentTableRow) => void
}

export type SearchActionsProviderProps = {
  children: React.ReactNode
  recentItems: RecentItem[]
  addRecentItem: (item: SearchableItem) => void
  onOpenChatbot: () => void
  onSnapshotSearchResults: (tabId: string, results: DocumentTableRow[]) => void
  favoritesDisabled?: boolean
  favoritesSaving?: boolean
  isFavorited: (id: string) => boolean
  toggleFavorite: (doc: DocumentTableRow) => void
}

const SearchActionsContext = createContext<
  SearchActionsContextType | undefined
>(undefined)

export function SearchActionsProvider({
  children,
  recentItems,
  addRecentItem,
  onOpenChatbot,
  onSnapshotSearchResults,
  isFavorited,
  toggleFavorite,
  favoritesDisabled = false,
  favoritesSaving = false,
}: SearchActionsProviderProps) {
  const {
    handleSelectDocument,
    addDocumentTab,
    activeTabId,
    isOnDocument,
    updateSearchHistoryItem,
    openTabs,
    setActiveTabId,
    addTab,
    siteId,
    setPagination: setGlobalPagination,
  } = useKnowledgeDocumentUXContext()
  const routerSearch: Record<string, unknown> = useSearch({ strict: false })
  const orgId =
    typeof routerSearch.orgId === 'string' ? routerSearch.orgId : undefined

  const openTrend = useOpenTrendResource(siteId, 'doc-search')
  const openSavedChart = useOpenSavedChartResource(siteId, orgId)
  const openNewProcessDataView = useOpenNewProcessDataView(siteId, orgId)

  const handleAddRecentDocument = useCallback(
    (doc: DocumentTableRow) => {
      addRecentItem(documentTableRowToSearchItem(doc))
    },
    [addRecentItem]
  )

  const handleOpenSensorDashboard = useCallback(
    (sensor: SensorSearchItem) => {
      addRecentItem(sensor)
      openTrend([sensor.sensorId], sensor.name || sensor.sensorId)
    },
    [addRecentItem, openTrend]
  )

  const handleOpenChartDashboard = useCallback(
    (chart: ChartSearchItem) => {
      addRecentItem(chart)
      openSavedChart(chart.id, chart.displayName || chart.name)
    },
    [addRecentItem, openSavedChart]
  )

  const handleOpenAssetDashboard = useCallback(
    (datapointIds: string[], label: string) => {
      openTrend(datapointIds, label || PROCESS_DATA_LABEL)
    },
    [openTrend]
  )

  const handleOpenLinkedDocuments = useCallback(
    (documentIds: string[], referenceText: string, unitId?: string) => {
      if (documentIds.length === 0 && !unitId) return

      setGlobalPagination({ pageIndex: 0, pageSize: 200 })
      const linkedDocsItem: SearchHistoryItem = {
        type: 'search',
        searchState: {
          contentFilter: 'documents',
          searchMode: 'normal',
          viewMode: 'table',
          query: '',
          filters: [],
          sorting: [{ id: 'documentTitle', desc: false }],
          pagination: { pageIndex: 0, pageSize: 200 },
          selectedDocumentId: null,
        },
        linkedDocuments: {
          referenceText,
          documentIds,
          unitId,
        },
      }

      const newTab: DocumentTab = {
        id: `linked-docs-${Date.now()}`,
        history: [linkedDocsItem],
        currentIndex: 0,
      }
      addTab(newTab)
    },
    [setGlobalPagination, addTab]
  )

  const handleOpenTagSearchDashboard = useCallback(() => {
    openNewProcessDataView()
  }, [openNewProcessDataView])

  const onSelectDocument = useCallback(
    ({
      documentId,
      query,
      fragmentHighlight,
      searchOriginQuery,
      pageNumber,
    }: SelectDocumentOptions) => {
      handleSelectDocument(
        documentId,
        query,
        fragmentHighlight,
        searchOriginQuery,
        pageNumber
      )
    },
    [handleSelectDocument]
  )

  const onOpenNewTab = useCallback(
    ({
      documentId,
      query,
      fragmentHighlight,
      searchOriginQuery,
      pageNumber,
    }: OpenNewTabOptions) => {
      addDocumentTab({
        documentId,
        pageNumber: pageNumber ?? fragmentHighlight?.pageNum,
        query,
        searchOriginQuery,
        fragmentHighlight,
      })
    },
    [addDocumentTab]
  )

  const onSearchStateChange = useCallback(
    (updates: SearchStateUpdate, options?: { tabId?: string }) => {
      const tabId = options?.tabId ?? activeTabId
      if (!options?.tabId && isOnDocument) {
        return
      }
      updateSearchHistoryItem(tabId, updates)
    },
    [activeTabId, isOnDocument, updateSearchHistoryItem]
  )

  const value = useMemo<SearchActionsContextType>(
    () => ({
      onSelectDocument,
      onOpenNewTab,
      onOpenChatbot,
      onSearchStateChange,
      onAddRecentDocument: handleAddRecentDocument,
      onAddRecentAsset: addRecentItem,
      onOpenSensorDashboard: handleOpenSensorDashboard,
      onOpenChartDashboard: handleOpenChartDashboard,
      onOpenAssetDashboard: handleOpenAssetDashboard,
      onOpenLinkedDocuments: handleOpenLinkedDocuments,
      onOpenTagSearchDashboard: handleOpenTagSearchDashboard,
      openTabs,
      onSwitchToTab: setActiveTabId,
      recentItems,
      onSnapshotSearchResults,
      isFavorited,
      toggleFavorite,
      favoritesDisabled,
      favoritesSaving,
    }),
    [
      onSelectDocument,
      onOpenNewTab,
      onOpenChatbot,
      onSearchStateChange,
      handleAddRecentDocument,
      addRecentItem,
      handleOpenSensorDashboard,
      handleOpenChartDashboard,
      handleOpenAssetDashboard,
      handleOpenLinkedDocuments,
      handleOpenTagSearchDashboard,
      openTabs,
      setActiveTabId,
      recentItems,
      onSnapshotSearchResults,
      isFavorited,
      toggleFavorite,
      favoritesDisabled,
      favoritesSaving,
    ]
  )

  return (
    <SearchActionsContext.Provider value={value}>
      {children}
    </SearchActionsContext.Provider>
  )
}

export function useSearchActions(): SearchActionsContextType {
  const ctx = useContext(SearchActionsContext)
  if (!ctx) {
    throw new Error(
      'useSearchActions must be used within a SearchActionsProvider'
    )
  }
  return ctx
}
